import React, { useMemo, useState } from "react";
import swal from "sweetalert";
import Select from "../shared/Select";
import { components } from "react-select";
import { URL } from "../../../../utils/Constants";

const selectStyle = {
  control: {
    minWidth: "150px",
    width: "100%",
  },
};

const SubjectAssessmentTableRow = ({
  subjectId,
  admissionId,
  handleChange,
  studentSubjectAssessments,
  scale,
  hasTheory,
  hasPractical,
  isFinalized,
}) => {
  const currentRow = useMemo(() => {
    return (
      studentSubjectAssessments?.find(
        (item) =>
          item?.admissionId === admissionId && item?.subjectId === subjectId,
      ) || {}
    );
  }, [studentSubjectAssessments, admissionId, subjectId]);

  const theory = currentRow?.theory;
  const practical = currentRow?.practical;

  const selectOptions = useMemo(() => {
    if (scale.assessmentScaleTypeCode === "sct-lvl") {
      return (
        scale?.assessmentScaleLevels?.map((lvl) => ({
          label: lvl.level,
          value: lvl.id,
          levelType: lvl.levelType,
        })) || []
      );
    }

    if (scale.assessmentScaleTypeCode === "sct-grd") {
      return (
        scale?.assessmentScaleGradePoints?.map((grd) => ({
          label: grd.grade,
          value: grd.id,
        })) || []
      );
    }

    return [];
  }, [scale, scale?.assessmentScaleLevels]);

  const validateMarks = (max, min, value) => {
    if (value < min || value > max) {
      swal("Oops", `Marks must be between ${min} and ${max}`);
      return true;
    }

    return false;
  };

  const validateNegative = (e) => {
    if (e.key === "-" || e.key === "e") {
      e.preventDefault();
    }
  };

  const CustomOption = (props) => {
    const { data } = props;

    return (
      <components.Option {...props}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {data.levelType === "image" ? (
            <img
              src={URL.imageSelectURL + data.label}
              style={{ width: 20, marginRight: 10 }}
            />
          ) : (
            <span>{data.label}</span>
          )}
        </div>
      </components.Option>
    );
  };

  const CustomSingleValue = (props) => {
    const { data } = props;

    return (
      <components.SingleValue {...props}>
        <div style={{ display: "flex", alignItems: "center" }}>
          {data.levelType === "image" ? (
            <img
              src={URL.imageSelectURL + data.label}
              alt=""
              style={{
                width: 20,
                height: 20,
                objectFit: "cover",
                marginRight: 10,
                borderRadius: "50%",
              }}
            />
          ) : (
            <span>{data.label}</span>
          )}
        </div>
      </components.SingleValue>
    );
  };

  return (
    <>
      {/* theory */}
      {hasTheory && (
        <td>
          {scale.assessmentScaleTypeCode === "sct-mrk" ? (
            <input
              aria-label="theoryMarks"
              style={{
                width: "50px",
                height: "50px",
                margin: "0 auto",
                textAlign: "center",
              }}
              value={theory?.obtainedMarks ?? ""}
              type="number"
              min={0}
              onChange={(e) => {
                const value = Number(e?.target?.value);
                const max = Number(scale?.assessmentScaleMark?.theoryMax);
                const min = Number(scale?.assessmentScaleMark?.theoryMin);
                if (validateMarks(max, min, value)) {
                  return;
                }

                handleChange(
                  admissionId,
                  subjectId,
                  "obtainedMarks",
                  e?.target?.value,
                  "theory",
                  scale.id,
                  scale?.assessmentScaleMark?.id,
                );
              }}
              onKeyDown={validateNegative}
              className="form-control"
              disabled={theory?.isAbsent || isFinalized}
            />
          ) : scale.assessmentScaleTypeCode === "sct-lvl" ? (
            <Select
              options={selectOptions}
              components={{
                Option: CustomOption,
                SingleValue: CustomSingleValue,
              }}
              value={
                selectOptions?.find(
                  (opt) => opt.value === theory?.scaleLevelId,
                ) || null
              }
              onChange={(option) =>
                handleChange(
                  admissionId,
                  subjectId,
                  "scaleLevelId",
                  option?.value,
                  "theory",
                  scale.id,
                )
              }
              isDisabled={theory?.isAbsent || isFinalized}
              styles={selectStyle}
            />
          ) : (
            <Select
              options={selectOptions}
              value={
                selectOptions?.find((opt) => opt.value === theory?.scaleGpId) ||
                null
              }
              onChange={(option) =>
                handleChange(
                  admissionId,
                  subjectId,
                  "scaleGpId",
                  option?.value,
                  "theory",
                  scale.id,
                )
              }
              isDisabled={theory?.isAbsent || isFinalized}
              styles={selectStyle}
            />
          )}

          <div
            style={{ margin: "10px 0 0 0", justifyContent: "center" }}
            className="flex"
          >
            <input
              aria-label="theoryAbsent"
              type="checkbox"
              name="isAbsent"
              checked={theory?.isAbsent || false}
              style={{ margin: 0, accentColor: "#126696" }}
              className="checkbox"
              disabled={isFinalized}
              onChange={(e) => {
                handleChange(
                  admissionId,
                  subjectId,
                  "isAbsent",
                  e.target.checked,
                  "theory",
                  scale.id,
                );
              }}
            />
            <label htmlFor="isAbsent">Absent</label>
          </div>
        </td>
      )}

      {/* practical */}
      {hasPractical && (
        <td>
          {scale.assessmentScaleTypeCode === "sct-mrk" ? (
            <input
              style={{
                width: "50px",
                height: "50px",
                margin: "0 auto",
                textAlign: "center",
              }}
              aria-label="practicalMarks"
              value={practical?.obtainedMarks ?? ""}
              type="number"
              min={0}
              onChange={(e) => {
                const value = Number(e?.target?.value);
                const max = Number(scale?.assessmentScaleMark?.practicalMax);
                const min = Number(scale?.assessmentScaleMark?.practicalMin);
                if (validateMarks(max, min, value)) {
                  return;
                }

                handleChange(
                  admissionId,
                  subjectId,
                  "obtainedMarks",
                  e?.target?.value,
                  "practical",
                  scale.id,
                  scale?.assessmentScaleMark?.id,
                );
              }}
              onKeyDown={validateNegative}
              className="form-control"
              disabled={practical?.isAbsent || isFinalized}
            />
          ) : scale.assessmentScaleTypeCode === "sct-lvl" ? (
            <Select
              options={selectOptions}
              value={
                selectOptions?.find(
                  (opt) => opt.value === practical?.scaleLevelId,
                ) || null
              }
              components={{
                Option: CustomOption,
                SingleValue: CustomSingleValue,
              }}
              onChange={(option) =>
                handleChange(
                  admissionId,
                  subjectId,
                  "scaleLevelId",
                  option?.value,
                  "practical",
                  scale?.id,
                )
              }
              isDisabled={practical?.isAbsent || isFinalized}
              styles={selectStyle}
            />
          ) : (
            <Select
              options={selectOptions}
              value={
                selectOptions?.find(
                  (opt) => opt.value === practical?.scaleGpId,
                ) || null
              }
              onChange={(option) =>
                handleChange(
                  admissionId,
                  subjectId,
                  "scaleGpId",
                  option?.value,
                  "practical",
                  scale?.id,
                )
              }
              isDisabled={practical?.isAbsent || isFinalized}
              styles={selectStyle}
            />
          )}

          <div
            style={{ margin: "10px 0 0 0", justifyContent: "center" }}
            className="flex"
          >
            <input
              aria-label="practicalAbsent"
              type="checkbox"
              name="isAbsent"
              checked={practical?.isAbsent || false}
              style={{ margin: 0, accentColor: "#126696" }}
              className="checkbox"
              disabled={isFinalized}
              onChange={(e) => {
                handleChange(
                  admissionId,
                  subjectId,
                  "isAbsent",
                  e.target.checked,
                  "practical",
                  scale?.id,
                );
              }}
            />
            <label htmlFor="isAbsent">Absent</label>
          </div>
        </td>
      )}
    </>
  );
};

export default SubjectAssessmentTableRow;
